/**
 * Sentry 인증 로깅 유틸리티
 * 휴대폰 인증(Mobile-OK) 및 간편인증(EZ-IOK) 관련 로깅 전용
 */

import * as Sentry from '@sentry/react';

// 인증 컨텍스트 인터페이스
export interface AuthSentryContext {
  authMethod: 'mobile' | 'easy';
  authProvider?: string;
  txId?: string;
  userId?: number;
  deviceType?: 'WB' | 'MB' | 'MWV';
  sdkStatus?: {
    mobileOK: boolean;
    eziok: boolean;
  };
  rawResponse?: string;
  resultCode?: string;
  resultMsg?: string;
  usageCode?: string;
  serviceType?: string;
  // redirect 모드 관련 속성
  retType?: 'callback' | 'redirect';
  useRedirect?: boolean;
  redirect?: boolean;
  isInAppBrowser?: boolean;
  inAppBrowserType?: string | null;
  isIOSSafari?: boolean;
  isFlutterApp?: boolean;
  browserType?: 'WB' | 'MB';
  authProviderCode?: string;
  errorMsg?: string;
}

// 에러 레벨
type SentryLevel = 'fatal' | 'error' | 'warning' | 'info' | 'debug';

/**
 * 인증 컨텍스트 설정
 * Sentry 스코프에 인증 관련 컨텍스트 추가
 */
export function setAuthContext(context: Partial<AuthSentryContext>): void {
  Sentry.setContext('auth', {
    ...context,
    timestamp: new Date().toISOString(),
  });
}

/**
 * 인증 관련 breadcrumb 추가
 * 인증 흐름 추적용
 */
export function addAuthBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>,
  level: SentryLevel = 'info'
): void {
  Sentry.addBreadcrumb({
    message,
    category: `auth.${category}`,
    level,
    data: {
      ...data,
      timestamp: new Date().toISOString(),
    },
  });
}

/**
 * 인증 이벤트 로깅 (captureMessage)
 * info/warning 레벨 이벤트용
 */
export function logAuthEvent(
  message: string,
  level: SentryLevel = 'info',
  context?: Partial<AuthSentryContext>,
  tags?: Record<string, string>
): void {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext('auth', {
        ...context,
        timestamp: new Date().toISOString(),
      });
    }

    if (tags) {
      Object.entries(tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    scope.setTag('feature', 'identity-verification');
    scope.setLevel(level);

    Sentry.captureMessage(`[Auth] ${message}`, level);
  });
}

/**
 * 인증 에러 캡처 (captureException)
 * error/fatal 레벨 에러용
 */
export function logAuthError(
  error: Error | string,
  context?: Partial<AuthSentryContext>,
  tags?: Record<string, string>,
  level: SentryLevel = 'error'
): void {
  const errorObj = typeof error === 'string' ? new Error(error) : error;

  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext('auth', {
        ...context,
        timestamp: new Date().toISOString(),
      });
    }

    if (tags) {
      Object.entries(tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    scope.setTag('feature', 'identity-verification');
    scope.setLevel(level);

    // 인증 방식에 따른 태그 추가
    if (context?.authMethod) {
      scope.setTag('auth.method', context.authMethod);
    }
    if (context?.authProvider) {
      scope.setTag('auth.provider', context.authProvider);
    }

    Sentry.captureException(errorObj);
  });
}

/**
 * SDK 로드 성공 로깅
 */
export function logSDKLoadSuccess(sdkType: 'mobileOK' | 'eziok'): void {
  addAuthBreadcrumb(
    `${sdkType} SDK loaded successfully`,
    'sdk',
    { sdkType },
    'info'
  );
}

/**
 * SDK 로드 실패 로깅
 */
export function logSDKLoadError(
  sdkType: 'mobileOK' | 'eziok',
  error: Error
): void {
  logAuthError(error, { authMethod: sdkType === 'mobileOK' ? 'mobile' : 'easy' }, { 'auth.sdk': sdkType, 'auth.stage': 'sdk-load' }, 'fatal');
}

/**
 * 인증 시작 로깅
 */
export function logAuthStart(
  authMethod: 'mobile' | 'easy',
  params: {
    authProvider?: string;
    txId?: string;
    userId?: number;
    deviceType?: string;
    usageCode?: string;
    serviceType?: string;
    // redirect 모드 관련 속성
    retType?: 'callback' | 'redirect';
    useRedirect?: boolean;
    isInAppBrowser?: boolean;
    inAppBrowserType?: string | null;
    isIOSSafari?: boolean;
    isFlutterApp?: boolean;
  }
): void {
  addAuthBreadcrumb(
    `${authMethod} authentication started`,
    'start',
    params,
    'info'
  );

  setAuthContext({
    authMethod,
    ...params,
  } as Partial<AuthSentryContext>);
}

/**
 * 콜백 수신 로깅
 */
export function logAuthCallback(
  authMethod: 'mobile' | 'easy',
  rawResponse: string
): void {
  addAuthBreadcrumb(
    `${authMethod} callback received`,
    'callback',
    {
      rawResponse,
      responseLength: rawResponse.length,
    },
    'info'
  );
}

/**
 * 인증 성공 로깅
 */
export function logAuthSuccess(
  authMethod: 'mobile' | 'easy',
  context?: Partial<AuthSentryContext>
): void {
  addAuthBreadcrumb(
    `${authMethod} authentication succeeded`,
    'success',
    context as Record<string, unknown>,
    'info'
  );
}

/**
 * 인증 실패 로깅
 */
export function logAuthFailure(
  authMethod: 'mobile' | 'easy',
  errorMessage: string,
  context?: Partial<AuthSentryContext>
): void {
  logAuthError(
    new Error(errorMessage),
    {
      authMethod,
      ...context,
    },
    {
      'auth.method': authMethod,
      'auth.stage': 'verification',
    },
    'error'
  );
}

/**
 * API 호출 에러 로깅
 */
export function logAuthAPIError(
  authMethod: 'mobile' | 'easy',
  apiEndpoint: string,
  error: Error | unknown,
  context?: Partial<AuthSentryContext>
): void {
  const errorObj = error instanceof Error ? error : new Error(String(error));

  logAuthError(
    errorObj,
    {
      authMethod,
      ...context,
    },
    {
      'auth.method': authMethod,
      'auth.stage': 'api-call',
      'auth.api': apiEndpoint,
    },
    'warning'
  );
}

/**
 * 콜백 파싱 에러 로깅
 */
export function logCallbackParseError(
  authMethod: 'mobile' | 'easy',
  rawResponse: string,
  error: Error
): void {
  logAuthError(
    error,
    {
      authMethod,
      rawResponse,
    },
    {
      'auth.method': authMethod,
      'auth.stage': 'callback-parse',
    },
    'error'
  );
}
